import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { object, string, array } from 'yup';
import RightModalLayout from '../layouts/Modal/RightModalLayout';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';
import InputMC from '../components/InputMC';
import { createField, updateField } from '../../actions/customerRelatedFieldsActions';
import DropdownMC from '../components/DropdownMC';
import { CUSTOMER_RELATED_FIELDS } from '../../constants/AppConstants';
import ButtonMC from '../components/ButtonMC';
import FormProviderMC from '../components/FormProviderMC';

const CreateEditCustomerRelatedFields = ({
  fieldSelected,
  VARIANT = 'CREATE',
  onCreateSuccess,
  onEditSuccess
}) => {
  const { t } = useTranslation("CustomerRelatedFieldsMC");
  const dispatch = useDispatch();
  const isCreateVariant = VARIANT === 'CREATE';
  const title = isCreateVariant
    ? t('sidebar.title_create')
    : t('sidebar.title_edit');

  const schema = object().shape({
    name: string().required(t('sidebar.field_required')),
    field_type: string().required(t('sidebar.field_required')),
    list_options: array().when('field_type', {
      is: CUSTOMER_RELATED_FIELDS.list,
      then: () => array()
        .of(
          object().shape({
            key: string().required(t('sidebar.field_required')),
            value: string().required(t('sidebar.field_required'))
          })
        )
        .min(1, t('sidebar.list_options_required'))
        .test(
          'unique-keys',
          function (list_options) {
            if (!list_options) {
              return true;
            }
            const keys = list_options.map((option) => option.key);
            const uniqueKeys = new Set(keys);
            if (uniqueKeys.size === keys.length) {
              return true;
            }
            // eslint-disable-next-line react/no-this-in-sfc
            return this.createError({
              // eslint-disable-next-line react/no-this-in-sfc
              path: `${this.path}.root`,
              message: t('sidebar.list_options_unique')
            });
          }
        ),
      otherwise: () => array().nullable()
    })
  });

  const defaultValues = {
    ...fieldSelected,
    field_type: fieldSelected?.field_type || CUSTOMER_RELATED_FIELDS.text,
    list_options:
        fieldSelected?.list_options?.length > 0
          ? fieldSelected.list_options
          : [{ key: '', value: '', isNew: true }]
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
    getValues,
    clearErrors,
    setError
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'list_options'
  });

  useEffect(() => {
    if (!isCreateVariant) {
      setValue('name', fieldSelected.name);
      setValue('field_type', fieldSelected.field_type);
      setValue('list_options', fieldSelected.list_options);
    }
  }, [isCreateVariant, fieldSelected, setValue]);

  const fieldDTO = (fieldToTransform) => ({
    name: fieldToTransform.name,
    field_type: fieldToTransform.field_type,
    list_options_attributes: fieldToTransform.list_options
      ? fieldToTransform.list_options.reduce((acc, option, index) => {
        if (option.key && option.value) {
          acc[index + 1] = {
            key: option.key,
            value: option.value
          };
        }
        return acc;
      }, {})
      : {}
  });

  const onSubmit = async () => {
    const data = getValues();
    if (isCreateVariant) {
      try {
        await dispatch(createField({ customer_related_field: fieldDTO(data) }));
        onCreateSuccess();
        dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
        showtoast(t('sidebar.submit.success_create'));
      } catch (err) {
        if (err.message?.identifier) {
          err.message.identifier.forEach((errorMessage) => {
            setError('name', { type: 'server', message: errorMessage });
          });
        } else {
          setError('root', { type: 'server', message: t('sidebar.submit.failed_create') });
        }
      }
    } else {
      try {
        await dispatch(
          updateField({ customer_related_field: fieldDTO(data), id: fieldSelected.web_id })
        );
        onEditSuccess();
        dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
        showtoast(t('sidebar.submit.success_edit'));
      } catch (err) {
        if (err.message?.identifier) {
          err.message.identifier.forEach((errorMessage) => {
            setError('name', { type: 'server', message: errorMessage });
          });
          return;
        }

        if (err.message?.field_type) {
          err.message.field_type.forEach((errorMessage) => {
            setError('field_type', { type: 'server', message: errorMessage });
          });
          return;
        }

        setError('root', { type: 'server', message: t('sidebar.submit.failed_edit') });
      }
    }
  };

  const dropdownOptions = [
    { id: CUSTOMER_RELATED_FIELDS.text, label: t('field_type.string') },
    {
      id: CUSTOMER_RELATED_FIELDS.number,
      label: t('field_type.integer')
    },
    { id: CUSTOMER_RELATED_FIELDS.float, label: t('field_type.float') },
    {
      id: CUSTOMER_RELATED_FIELDS.boolean,
      label: t('field_type.boolean')
    },
    { id: CUSTOMER_RELATED_FIELDS.date, label: t('field_type.date') },
    { id: CUSTOMER_RELATED_FIELDS.list, label: t('field_type.list') }
  ];

  const handleAddOption = () => {
    append({ key: '', value: '', isNew: true });
    clearErrors('list_options.root');
  };

  const handleDeleteOption = (indexOption) => {
    remove(indexOption);
  };

  const selectedFieldType = watch('field_type');

  const renderListOption = (option, index) => (
    <div key={option.id} className="tw-flex tw-items-end tw-space-x-3 tw-mb-8">
      <InputMC
        id={`list_options.${index}.key`}
        name={`list_options.${index}.key`}
        label={t('sidebar.option_key')}
        disabled={!isCreateVariant && !option.isNew}
        className={`${
          !isCreateVariant && !option.isNew ? 'tw-opacity-85 tw-cursor-not-allowed' : ''
        }`}
        useFloatingLabel
      />
      <InputMC
        id={`list_options.${index}.value`}
        name={`list_options.${index}.value`}
        label={t('sidebar.option_value')}
        useFloatingLabel
      />
      <ButtonMC
        text="X"
        onClick={() => handleDeleteOption(index)}
        variant="DANGER"
        outlined
        className="!tw-h-10 !tw-w-10 !tw-px-4"
      />
    </div>
  );

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={title}
        submitAction={handleSubmit(onSubmit)}
        cancelAction={() => dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR })}
        submitLabel={isCreateVariant ? t('sidebar.save') : t('sidebar.update')}
        cancelLabel={t('sidebar.cancel')}
      >
        <FormProviderMC
          methods={methods}
          onSubmit={handleSubmit(onSubmit)}
          className="tw-space-y-4"
        >
          {errors?.root && (
            <p className="tw-text-red-500 tw-text-sm">{errors.root.message}</p>
          )}
          <InputMC
            id="name"
            name="name"
            label={t('sidebar.name')}
            className="tw-resize-none"
            useFloatingLabel
          />
          <DropdownMC
            id="field_type"
            name="field_type"
            label={t('sidebar.field_type')}
            options={dropdownOptions}
            defaultValue={selectedFieldType}
            useFloatingLabel
          />

          {selectedFieldType === CUSTOMER_RELATED_FIELDS.list && (
            <>
              <p className="tw-font-bold">{t('sidebar.list_title')}</p>
              <p className="tw-text-base">
                {t('sidebar.list_description')}
              </p>
              {errors.list_options?.root && (
                <p className="tw-text-red-500 tw-text-sm">{errors.list_options.root.message}</p>
              )}
              {fields.map((option, index) => renderListOption(option, index))}
              <ButtonMC
                text={t('sidebar.add_option')}
                onClick={handleAddOption}
              />
            </>
          )}
        </FormProviderMC>
      </RightModalLayout>
    </div>
  );
};

export default CreateEditCustomerRelatedFields;
